import React, { useMemo } from "react";
import { Card, Divider } from "antd";
import { connect } from "dva";
import { ShipmentIncompleteStatus } from "@/typedefs/shipment";
import { ShipmentStatus } from "@/utils/enum";
import { formatPrice } from "@/utils/utils";
import { DryRunTag } from "@/components/Tags";

const TotalPriceCard = ({ adminShipmentView }) => {
  const adminShipmentData = useMemo(() => adminShipmentView?.data, [adminShipmentView]);

  const { dealer: totalDealerPrice, transporter: totalTransporterPrice } = useMemo(() => {
    if (!adminShipmentData) {
      return {
        dealer: 0,
        transporter: 0,
      };
    }

    return adminShipmentData.vehicles.reduce(
      (prevValue, vehicleData) => {
        const { quote, pricingEngineResult } = vehicleData;

        return {
          dealer: prevValue.dealer + (pricingEngineResult?.dealerPrice || quote?.dealerPrice || 0),
          transporter: prevValue.transporter + (pricingEngineResult?.transporterPrice || quote?.transporterPrice || 0),
        };
      },
      { dealer: 0, transporter: 0 },
    );
  }, [adminShipmentData]);

  const transporterPricePerMile = useMemo(() => {
    if (!adminShipmentData) {
      return 0;
    }

    const numVehicles = adminShipmentData.vehicles.length > 0 ? adminShipmentData.vehicles.length : 1;

    const miles = adminShipmentData.distance ? adminShipmentData.distance : 1;

    const pricePerMile = totalTransporterPrice / numVehicles / miles;
    return formatPrice(pricePerMile);
  }, [adminShipmentData, totalTransporterPrice]);

  const isDryRun = useMemo(
    () =>
      adminShipmentData?.status === ShipmentStatus.Incomplete &&
      adminShipmentData?.load?.loadIncomplete?.status === ShipmentIncompleteStatus.DRY_RUN,
    [adminShipmentData],
  );

  if (!adminShipmentData) {
    return null;
  }

  return (
    <Card
      style={{
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "49% 2% 49%",
        }}
      >
        <div
          style={{
            padding: 12,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "100px",
          }}
        >
          <div>
            <div style={{ color: "rgba(0, 0, 0, 0.85)" }}>Total Dealer Price</div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 600,
                textDecoration: isDryRun ? "line-through" : "none",
                opacity: isDryRun ? 0.5 : 1,
              }}
            >
              {formatPrice(totalDealerPrice)}
            </div>
          </div>
          {isDryRun && (
            <div>
              <DryRunTag price={adminShipmentData?.load?.loadIncomplete?.dryRunPrice || 0} />
            </div>
          )}
        </div>
        <Divider
          style={{
            height: "100%",
            borderLeft: "2px solid rgba(0, 0, 0, 0.1)",
            borderTop: "unset",
            margin: 0,
          }}
        />
        <div
          style={{
            padding: 12,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "100px",
          }}
        >
          <div>
            <div style={{ color: "rgba(0, 0, 0, 0.85)" }}>Total Transporter Price</div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 600,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                textDecoration: isDryRun ? "line-through" : "none",
                opacity: isDryRun ? 0.5 : 1,
              }}
            >
              <span style={{ marginRight: 16 }}>{formatPrice(totalTransporterPrice)}</span>
              <span
                style={{
                  fontSize: 16,
                  textDecoration: isDryRun ? "line-through" : "none",
                  opacity: isDryRun ? 0.5 : 1,
                }}
              >
                {transporterPricePerMile} $/mi
              </span>
            </div>
          </div>
          {isDryRun && (
            <div>
              <DryRunTag price={adminShipmentData?.load?.loadIncomplete?.dryRunCost || 0} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default connect(({ adminShipmentView }) => ({
  adminShipmentView,
}))(TotalPriceCard);
